import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { gerarPDFHistorico } from '../utils/pdf';

const STATUS = {
  orcamento:            { label: 'Ag. Avaliação do Mecânico', cor: 'bg-gray-100 text-gray-700' },
  aprovado:             { label: 'Aprovado - ag. mecânico', cor: 'bg-blue-100 text-blue-800' },
  em_andamento:         { label: 'Em andamento/Em conserto', cor: 'bg-orange-100 text-orange-800' },
  aguardando_orcamento: { label: 'Enviar orçamento p/ aprovação do cliente', cor: 'bg-purple-100 text-purple-800' },
  aguardando_cliente:   { label: 'Aguardando aprovação do cliente', cor: 'bg-cyan-100 text-cyan-800' },
  concluido:            { label: 'Concluído',              cor: 'bg-green-100 text-green-800' },
  cancelado:            { label: 'Orçamento Não Aprovado', cor: 'bg-red-100 text-red-800' },
};

function GarantiaInfo({ garantia }) {
  if (!garantia?.fornecedor) return null;
  return (
    <div className="text-xs text-blue-600 mt-0.5 flex flex-wrap gap-x-3">
      <span>🛡 {garantia.fornecedor}</span>
      {garantia.notaFiscal && <span>NF: {garantia.notaFiscal}</span>}
      {garantia.dataCompra && <span>Compra: {new Date(garantia.dataCompra + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
      {garantia.diasGarantia && (() => {
        const venc = new Date(garantia.dataCompra + 'T00:00:00');
        venc.setDate(venc.getDate() + Number(garantia.diasGarantia));
        const diff = Math.ceil((venc - new Date()) / 86400000);
        const cor = diff < 0 ? 'text-red-500' : diff < 30 ? 'text-orange-500' : 'text-green-600';
        return <span className={cor}>{diff < 0 ? `Garantia vencida (${Math.abs(diff)}d)` : `Garantia até ${venc.toLocaleDateString('pt-BR')}`}</span>;
      })()}
    </div>
  );
}

export default function Historico() {
  const { veiculoId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [veiculo, setVeiculo] = useState(null);
  const [servicos, setServicos] = useState([]);
  const [expandido, setExpandido] = useState(null);
  const [pecasAberto, setPecasAberto] = useState(true);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get(`/veiculos/${veiculoId}`).then(r => setVeiculo(r.data));
    api.get(`/servicos/veiculo/${veiculoId}`).then(r => setServicos(r.data));
    api.get('/configuracao').then(r => setConfig(r.data)).catch(() => setConfig(null));
  }, [veiculoId]);

  if (!veiculo) return <div className="flex items-center justify-center h-64 text-gray-500">Carregando...</div>;

  const totalGasto = servicos.filter(s => s.status === 'concluido').reduce((a, s) => a + (s.totalGeral || 0), 0);

  // Lista consolidada de todas as peças já trocadas neste veículo, mais recentes primeiro
  const todasPecas = servicos
    .flatMap(s => (s.pecas || []).map(p => ({ ...p, servicoNumero: s.numero, servicoData: s.dataEntrada, servicoId: s._id })))
    .sort((a, b) => new Date(b.servicoData) - new Date(a.servicoData));

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="text-blue-600 text-sm mb-4 hover:underline">← Voltar</button>

      {/* Card do veículo */}
      <div className="bg-white rounded-xl shadow-sm p-5 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{veiculo.placa || '(sem placa)'}</h1>
            <p className="text-gray-600 font-medium">{veiculo.marca} {veiculo.modelo} {veiculo.ano}</p>
            <p className="text-sm text-gray-400 mt-0.5">{veiculo.cor} • {veiculo.combustivel} • {Number(veiculo.km).toLocaleString()} km</p>
          </div>
          <button
            onClick={() => navigate(`/servicos/novo?veiculoId=${veiculo._id}&clienteId=${veiculo.clienteId || veiculo.cliente?._id}`)}
            className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 shrink-0"
          >
            + Nova OS
          </button>
        </div>

        {veiculo.cliente && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">Proprietário: <strong className="text-gray-700">{veiculo.cliente.nome}</strong></span>
            <span className="text-sm text-gray-500">{veiculo.cliente.telefone}</span>
          </div>
        )}

        {/* Resumo */}
        <div className={`mt-4 grid gap-3 ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-800">{servicos.length}</div>
            <div className="text-xs text-gray-500 mt-0.5">Total de OS</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{servicos.filter(s => s.status === 'concluido').length}</div>
            <div className="text-xs text-gray-500 mt-0.5">Concluídas</div>
          </div>
          {isAdmin && (
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-blue-600">R$ {totalGasto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
              <div className="text-xs text-gray-500 mt-0.5">Total investido</div>
            </div>
          )}
        </div>
      </div>

      {/* Peças trocadas e garantias — consolidado de todas as OS */}
      <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3">
          <button type="button" onClick={() => setPecasAberto(v => !v)}
            className="flex items-center gap-2 flex-1 min-w-0 text-left">
            <span className={`text-lg font-bold text-blue-600 transition-transform shrink-0 ${pecasAberto ? 'rotate-45' : ''}`}>+</span>
            <span className="font-semibold text-gray-700">🛡 Peças Trocadas e Garantias</span>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{todasPecas.length}</span>
          </button>
          <button
            onClick={() => gerarPDFHistorico(veiculo, servicos, config, isAdmin)}
            className="bg-gray-800 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-900 shrink-0"
          >
            📄 Gerar PDF
          </button>
        </div>
        {pecasAberto && (
          <div className="px-4 pb-4">
            {todasPecas.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nenhuma peça registrada para este veículo</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {todasPecas.map((p, i) => (
                  <div key={i} className="py-2.5">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-800">{p.descricao}</span>
                        <span className="text-gray-400 text-sm"> ({p.quantidade}x)</span>
                        <div className="text-xs text-gray-400">
                          OS #{p.servicoNumero}{p.servicoData && ` • ${format(new Date(p.servicoData), 'dd/MM/yyyy', { locale: ptBR })}`}
                        </div>
                      </div>
                      {isAdmin && <span className="text-sm font-semibold text-gray-700 shrink-0">R$ {p.valorTotal?.toFixed(2)}</span>}
                    </div>
                    {p.numeroSerie && <div className="text-xs text-gray-400 mt-0.5">Nº Série: <span className="font-mono">{p.numeroSerie}</span></div>}
                    {p.garantia?.fornecedor
                      ? <GarantiaInfo garantia={p.garantia} />
                      : <div className="text-xs text-gray-300 italic mt-0.5">Sem dados de garantia registrados</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lista de OS */}
      <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
        Ordens de Serviço
        <span className="text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{servicos.length}</span>
      </h2>

      {servicos.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-10 text-center">
          <div className="text-4xl mb-3">🔧</div>
          <p className="text-gray-500 font-medium">Nenhuma OS registrada</p>
          <p className="text-gray-400 text-sm mt-1">Este veículo ainda não possui histórico de serviços.</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-50">
        {servicos.map(s => {
          const aberto = expandido === s._id;
          return (
            <div key={s._id}>
              {/* Linha principal */}
              <div
                className="p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 active:bg-gray-100"
                onClick={() => setExpandido(aberto ? null : s._id)}
              >
                {/* Ícone status */}
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  s.status === 'concluido' ? 'bg-green-500' :
                  s.status === 'em_andamento' ? 'bg-orange-400' :
                  s.status === 'aprovado' ? 'bg-blue-500' :
                  s.status === 'aguardando_orcamento' ? 'bg-purple-500' :
                  s.status === 'cancelado' ? 'bg-red-400' : 'bg-yellow-400'
                }`} />

                {/* Número OS + data */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">OS #{s.numero}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS[s.status]?.cor}`}>
                      {STATUS[s.status]?.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-sm text-gray-500">
                    <span>📅 Entrada: {format(new Date(s.dataEntrada), 'dd/MM/yyyy', { locale: ptBR })}</span>
                    {s.dataConclusao && (
                      <span>✅ Conclusão: {format(new Date(s.dataConclusao), 'dd/MM/yyyy', { locale: ptBR })}</span>
                    )}
                  </div>
                  {s.diagnostico && (
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{s.diagnostico}</p>
                  )}
                </div>

                {/* Valor (admin) + km + seta */}
                <div className="text-right flex-shrink-0">
                  {isAdmin && (
                    <div className="font-semibold text-gray-800">R$ {s.totalGeral?.toFixed(2)}</div>
                  )}
                  {s.kmEntrada && <div className="text-xs text-gray-400">{Number(s.kmEntrada).toLocaleString()} km</div>}
                  <div className="text-gray-400 text-xs mt-1">{aberto ? '▲' : '▼'}</div>
                </div>
              </div>

              {/* Detalhes expandidos */}
              {aberto && (
                <div className="bg-gray-50 px-4 pb-4 space-y-3">
                  {/* Peças */}
                  {s.pecas?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Peças e Materiais</p>
                      <div className="space-y-2">
                        {s.pecas.map((p, i) => (
                          <div key={i} className="text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">{p.descricao} <span className="text-gray-400">({p.quantidade}x)</span></span>
                              {isAdmin && <span className="font-medium text-gray-700">R$ {p.valorTotal?.toFixed(2)}</span>}
                            </div>
                            {p.numeroSerie && <div className="text-xs text-gray-400 mt-0.5">Nº Série: <span className="font-mono">{p.numeroSerie}</span></div>}
                            <GarantiaInfo garantia={p.garantia} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mão de obra */}
                  {s.maosDeObra?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Mão de Obra</p>
                      <div className="space-y-1">
                        {s.maosDeObra.map((m, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-gray-600">{m.descricao}</span>
                            {isAdmin && <span className="font-medium text-gray-700">R$ {m.valor?.toFixed(2)}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Totais — apenas admin */}
                  {isAdmin && (
                    <div className="border-t border-gray-200 pt-2 space-y-1">
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Total Peças</span><span>R$ {s.totalPecas?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Total Mão de Obra</span><span>R$ {s.totalMaoDeObra?.toFixed(2)}</span>
                      </div>
                      {s.desconto > 0 && (
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>Desconto</span><span>- R$ {s.desconto?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-gray-800 text-sm pt-1 border-t border-gray-200">
                        <span>Total</span><span>R$ {s.totalGeral?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {s.responsavel && (
                    <p className="text-xs text-gray-400">Responsável: {s.responsavel}</p>
                  )}

                  {/* Botão ver OS completa */}
                  <button
                    onClick={() => navigate(`/servicos/${s._id}`, { state: { from: 'historico' } })}
                    className="w-full border border-blue-300 text-blue-600 py-2 rounded-lg text-sm font-medium hover:bg-blue-50"
                  >
                    Ver OS completa →
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
